/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { CircularProgress, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { colors } from 'src/constants';
import { TemplateDetails, TemplateList } from 'src/types';
import { isEmpty, find, debounce } from 'lodash';
import useQuotationApi from 'src/hooks/quotation-v2/use-quotation-api';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';

const useStyles = makeStyles((theme) => ({
  autoComplete: {
    borderBottomWidth: 1,
    borderBottomColor: 'pink'
  },
  input: {
    color: colors.blue[500],
    fontWeight: 500
  }
}));

interface TemplateProps {}

const Templates = () => {
  const classes = useStyles();

  const {
    is_template_loading,
    templateOptions,
    fetchTemplate,
    fetchTemplateDetails
  } = useQuotationApi();
  const {
    quotationParams,
    templateDetails,
    resetQuotation,
    setQuotationParams
  } = useQuotationProduct();

  const processGetTemplates = (keyword: string, branch_id: number) => {
    let exist = false;
    if (!isEmpty(templateOptions)) {
      exist = !isEmpty(find(templateOptions, { name: keyword }));
    }

    if (!exist) {
      fetchTemplate({ keyword, branch_id: branch_id });
    }
  };

  const onHandleTemplateChange = (template_id: number, branch_id: number) => {
    if (template_id === 0) {
      resetQuotation();
      setQuotationParams({
        ...quotationParams,
        boost: 0,
        pc_build: 0
      });
      return;
    }
    fetchTemplateDetails({ branch_id, template_id });
  };

  const debouncedTemplateInputFetch = React.useMemo(
    () =>
      debounce((keyword: string, branch_id: number) => {
        processGetTemplates(keyword, branch_id);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quotationParams]
  );

  useEffect(() => {
    if (quotationParams?.branch_id) {
      processGetTemplates('', quotationParams?.branch_id || 1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationParams?.branch_id]);

  return (
    <div>
      <Autocomplete
        fullWidth
        size="medium"
        id="template-list"
        className={classes.autoComplete}
        options={templateOptions}
        value={templateDetails}
        getOptionSelected={(option, value) => option?.id === value?.id}
        onChange={(_, newValue) =>
          onHandleTemplateChange(
            newValue?.id || 0,
            quotationParams?.branch_id || 1
          )
        }
        getOptionLabel={(option) => option.name ?? ''}
        onInputChange={(_, newInputValue) => {
          debouncedTemplateInputFetch(
            newInputValue,
            quotationParams?.branch_id || 1
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={'Template'}
            variant="outlined"
            placeholder="Select Template"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {is_template_loading && (
                    <CircularProgress color="primary" size={20} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </div>
  );
};

export const TemplateOption = React.memo(Templates);
